import { toast } from 'react-toastify';
import { table_config } from './helper';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GmModal from 'app/shared/modal/modal';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';
import WebDataTable from 'app/shared/datatable/web/datatable';
import ConfirmationDialog from 'app/shared/dialogs/confirmation';
import ImportContact from 'app/member/modules/contacts/import/import';
import { PageContentWrapper } from 'app/layouts/wrapper/page-content';
import {
  loadContactsToStore,
  removeManyContactsFromStore,
  removeOneContactFromStore,
  updateContactInStore
} from 'store/actions/contact';
import { setPageTitle } from 'store/actions/header';
import AddContactToAudience from '../add-to-audience/add-to-audience';
import { Spacer } from 'app/layouts/generic';
import { SegmentedTabs } from 'app/shared/segmented-tabs';
import styles from './list.module.css';
import { ContactStatBar } from './statbar';
import { CreateContactModal } from '../create';
import useScreenSize from 'hooks/size';
import { useContactService } from 'hooks/users/contact';
import { PermissionsContext } from 'contexts/permissions';
import { ContactListTagManager } from './tag/manage';
import { useAudienceService } from 'hooks/users/audience';
import { filter_config } from './config/filter';
import { ContactEventTrigger } from '../event/trigger';
import SplitButton from 'app/shared/button/split';
import { CreateFreeformContact } from '../freeform';

// Validity is a tab rather than a filter; each value is the bool= query it sends.
const validity_tabs = [
  { value: 'is_valid', label: 'Valid' },
  { value: '-is_valid', label: 'Invalid' }
];

const ListContacts = () => {
  const { permissions } = useContext(PermissionsContext);
  const { is_mobile_view } = useSelector((state) => state.metadata);
  const contacts_in_store = useSelector((state) => state.contacts);
  const { isMobile } = useScreenSize();
  const { fetchAudiences } = useAudienceService();
  const {
    downloadContact,
    fetchContacts,
    searchContact,
    updateContact,
    removeContact,
    removeContacts
  } = useContactService();

  const dispatch = useDispatch();
  const history = useNavigate();

  const [audiences, setAudiences] = useState([]);
  const [bulk_contact_to_delete, setBulkContactsToDelete] = useState([]);
  const [contact_count, setContactCount] = useState(0);
  const [contact_to_delete, setContactToDelete] = useState(0);
  const [is_search_mode, setSearchMode] = useState(false);
  const [items, setItems] = useState([]);
  const [loading_data, setLoadingData] = useState(false);
  const [selected_contacts, setSelectedContacts] = useState([]);
  const [show_creation_modal, setShowCreationModal] = useState(false);
  const [show_freeform_modal, setShowFreeformModal] = useState(false);
  const [show_upload_modal, setShowUploadModal] = useState(false);
  const [show_tag_modal, setShowTagModal] = useState(false);
  const [show_contact_link_modal, setShowContactLinkModal] = useState(false);
  const [show_deletion_confirmation, setShowDeletionConfirmation] = useState(false);
  const [show_download_confirmation, setShowDownloadConfirmation] = useState(false);
  const [show_event_modal, setShowEventModal] = useState(false);
  const [unselect_items, setUnSelectItems] = useState(true);
  const [validity, setValidity] = useState('is_valid');

  useEffect(() => {
    dispatch(setPageTitle([{ title: 'Contacts', path: '.' }]));

    fetchAudiences({ query_string: 'bool=-is_archived' }).then(({ audiences }) => {
      const parsed_audiences = [];
      for (const audience of audiences) {
        parsed_audiences.push({ label: audience.name, value: audience.id });
      }

      setAudiences(parsed_audiences);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setItems(Object.values(contacts_in_store));
  }, [contacts_in_store]);

  const deleteContact = async (id) => {
    await removeContact(id);
    dispatch(removeOneContactFromStore(id));
    toast.success('contact deleted successfully.');
    dispatch(removeOneContactFromStore(id));
  };

  const deleteBulkContact = async (ids) => {
    const data = {
      options: {
        id: ids.toString()
      }
    };
    await removeContacts({ data });

    dispatch(removeManyContactsFromStore(ids));
    toast.success('contacts deleted successfully.');
  };

  const handleDeletionConfirmation = (permitted) => {
    if (permitted) {
      if (bulk_contact_to_delete.length > 0) deleteBulkContact(bulk_contact_to_delete);
      else deleteContact(contact_to_delete);
    }

    setContactToDelete(0);
    setBulkContactsToDelete([]);
    setShowDeletionConfirmation(false);
  };

  const handleDownloadConfirmation = async (permitted) => {
    if (permitted) {
      const result = await downloadContact();
      if (result)
        toast.success('Request submitted successfully. Download link will be sent to your email. ');
    }

    setShowDownloadConfirmation(false);
  };

  const updateContactStatus = async (data) => {
    const payload = await updateContact(data.id, { data: { is_valid: true } });
    if (payload) {
      toast.success('Contact updated successfully.');
      // A contact marked valid no longer belongs on the Invalid tab.
      if (validity === '-is_valid') {
        dispatch(removeOneContactFromStore(data.id));
        setContactCount((count) => Math.max(count - 1, 0));
        return;
      }
      dispatch(updateContactInStore({ ...data, is_valid: true }));
    }
  };

  const handleDatatableAction = async (action) => {
    const { name, type, data } = action;
    if (type.toLowerCase() === 'bulk') {
      switch (name.toLowerCase()) {
        case 'add_to_audience':
          setSelectedContacts(data);
          setShowContactLinkModal(true);
          break;
        case 'assign_tag':
          setSelectedContacts(data);
          setShowTagModal(true);
          break;
        case 'export':
          setShowDownloadConfirmation(true);
          break;
        case 'delete':
          setBulkContactsToDelete(data.map((item) => item.id));
          setShowDeletionConfirmation(true);
          break;
        default:
      }
    }

    if (type.toLowerCase() === 'single') {
      switch (name.toLowerCase()) {
        case 'add_to_audience':
          setSelectedContacts([data]);
          setShowContactLinkModal(true);
          break;
        case 'delete':
          setContactToDelete(data.id);
          setShowDeletionConfirmation(true);
          break;
        case 'trigger_event':
          setSelectedContacts([data]);
          setShowEventModal(true);
          break;
        case 'manage_tag':
          setSelectedContacts([data]);
          setShowTagModal(true);
          break;
        case 'mark_as_valid':
          updateContactStatus(data);
          break;
        default:
      }
    }
  };

  const handleCreation = (action) => {
    switch (action.toLowerCase()) {
      case 'add_single':
        setShowCreationModal(true);
        break;
      case 'import_many':
        setShowUploadModal(true);
        break;
      case 'free_form':
        setShowFreeformModal(true);
        break;
      default:
    }
  };

  const handleItemClick = (data) => {
    history(`/contacts/${data.id}`);
  };

  const handleDataRequest = async (
    page,
    population = 10,
    options = { filter_query: '', sort_query: '', reset: false }
  ) => {
    try {
      setLoadingData(true);
      const sort_query = options.sort_query || 'sort_by=-created_on';
      const exclusion_query = `return_only=-subscriptions,-unsubscriptions,-unsubscription_history,-subscription_history,-extra_data,-events`;
      const { contacts, size, error } = await fetchContacts({
        query_string: `bool=${validity}&${options.filter_query}&${sort_query}&${exclusion_query}&page=${page}&population=${population}`
      });
      if (error) {
        return;
      }

      setContactCount(size);
      // Server-paginated table: every page replaces the current rows rather than
      // appending to them, so the list doesn't grow as you paginate.
      dispatch(loadContactsToStore(contacts));
    } catch (e) {
      dispatch(loadContactsToStore([]));
    } finally {
      setLoadingData(false);
    }
  };

  const handleSearchRequest = async (
    keys,
    keyword,
    page,
    population = 10,
    options = { filter_query: '', sort_query: '' }
  ) => {
    if (!keys) return;
    try {
      setLoadingData(true);

      const exclusion_query = `return_only=-subscriptions,-unsubscriptions,-unsubscription_history,-subscription_history,-extra_data,-events`;
      const sort_query = options.sort_query || 'sort_by=-created_on';
      const { contacts, size } = await searchContact(keys, keyword, {
        query_string: `bool=${validity}&${options.filter_query}&${sort_query}&${exclusion_query}&page=${page}&population=${population}`
      });

      setContactCount(size);
      // Replace (not append) so paginated search results show one page at a time.
      dispatch(loadContactsToStore(contacts));
    } catch (e) {
      dispatch(loadContactsToStore([]));
    } finally {
      setLoadingData(false);
    }
  };

  const creation_options = [
    [
      { label: 'Add contact', value: 'add_single' },
      { label: 'Import from file', value: 'import_many' },
      { label: 'Copy and paste', value: 'free_form' },
      { label: 'Download contacts', value: 'export' }
    ]
  ];

  const handlePrimaryAction = (name) => {
    // 'export' is a bulk/table action; the rest are contact-creation actions.
    if (name === 'export') return handleDatatableAction({ name, type: 'bulk' });
    handleCreation(name);
  };

  const handleValidityChange = (value) => {
    dispatch(loadContactsToStore([]));
    setValidity(value);
  };

  const validity_toggle = (
    <SegmentedTabs
      options={validity_tabs}
      value={validity}
      onChange={handleValidityChange}
      label="Contact validity"
    />
  );

  const table_actions = (
    <>
      {permissions['contact:create'] && (
        <span className={styles.creationOptions}>
          <SplitButton
            actions={creation_options}
            callback={handlePrimaryAction}
            separator_before_last
          />
        </span>
      )}
    </>
  );

  return (
    <PageContentWrapper>
      {!isMobile && (
        <>
          <ContactStatBar />
          <Spacer multiple={4} />
        </>
      )}
      {is_mobile_view ? (
        <>
          {validity_toggle}
          <Spacer multiple={2} />
          <MobileDatatable
            key={validity}
            config={{
              ...table_config(permissions),
              is_search_mode,
              items,
              total_count: contact_count
            }}
            action={handleDatatableAction}
            onClick={handleItemClick}
            onListModeChange={setSearchMode}
            onDataRequest={handleDataRequest}
            onSearchRequest={handleSearchRequest}
            showHeader
          />
        </>
      ) : (
        <div>
          <WebDataTable
            config={{
              ...table_config(permissions),
              is_search_mode,
              items,
              total_count: contact_count
            }}
            filter_config={filter_config(audiences)}
            has_filter
            header_tabs={validity_toggle}
            reset_key={validity}
            action={handleDatatableAction}
            onClick={handleItemClick}
            checkbox
            loading_data={loading_data}
            table_actions={table_actions}
            onDataRequest={handleDataRequest}
            onSearchRequest={handleSearchRequest}
            resetBulkSelection={unselect_items}
          />
        </div>
      )}
      <GmModal
        bodyClassName={styles.importsModal}
        show_title={true}
        title="Add Contacts to Audience"
        show_modal={show_contact_link_modal}
        onClose={() => setShowContactLinkModal(false)}
      >
        <AddContactToAudience
          selected_contacts={selected_contacts}
          show_modal={show_contact_link_modal}
          handleBulkUnselection={() => setUnSelectItems(!unselect_items)}
          onClose={() => setShowContactLinkModal(false)}
        />
      </GmModal>
      <GmModal
        bodyClassName={styles.importsModal}
        title="Create Contact"
        show_title={true}
        show_modal={show_creation_modal}
        onClose={() => setShowCreationModal(false)}
      >
        <CreateContactModal onClose={() => setShowCreationModal(false)} />
      </GmModal>
      <GmModal
        bodyClassName={styles.importsModal}
        title="Import Contacts"
        show_title={true}
        show_modal={show_upload_modal}
        onClose={() => setShowUploadModal(false)}
      >
        <ImportContact onDataRequest={handleDataRequest} />
      </GmModal>
      <GmModal
        bodyClassName={styles.freeformModal}
        title="Copy and Paste Contacts"
        show_title={true}
        show_modal={show_freeform_modal}
        onClose={() => setShowFreeformModal(false)}
      >
        <CreateFreeformContact onClose={() => setShowFreeformModal(false)} />
      </GmModal>
      <GmModal
        bodyClassName={styles.importsModal}
        title="Manage Tags"
        show_title={true}
        show_modal={show_tag_modal}
        onClose={() => setShowTagModal(false)}
      >
        <ContactListTagManager contacts={selected_contacts} />
      </GmModal>
      <GmModal
        bodyClassName={styles.eventModalWrapper}
        title="Trigger an event on a contact"
        show_title
        show_modal={show_event_modal}
        onClose={() => setShowEventModal(false)}
      >
        <ContactEventTrigger contact_data={selected_contacts[0]} />
      </GmModal>

      <ConfirmationDialog
        title="Delete contact?"
        type="error"
        icon_name="trash"
        action_text="Yes, delete"
        message="Are you sure you want to delete this contact? This action cannot be undone."
        callback={handleDeletionConfirmation}
        is_open={show_deletion_confirmation}
      />

      <ConfirmationDialog
        icon_name="info"
        title="Export contacts"
        message="You are about to export your contacts. If successful, a download link will be sent your email"
        callback={handleDownloadConfirmation}
        is_open={show_download_confirmation}
      />
    </PageContentWrapper>
  );
};

export default ListContacts;
